import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import TvOutlined from '@mui/icons-material/TvOutlined';
import { deliveryLabel, type WarehouseTask, type WarehouseTasks } from '@zilian/contracts';
import { api, imageUrl } from '../api';
import LineQuantityDialog, { type QuantityLine } from '../LineQuantityDialog';
import ReceiveDialog from '../ReceiveDialog';
import { usePickingSheets } from '../PickingSheet';
import {
  accessoriesText,
  amount,
  CountTabs,
  Empty,
  Heading,
  ListToolbar,
  Props,
  RowAction,
  Tag,
  usePagination,
  FitTableContainer,
} from '../ui';

type View = 'ship' | 'receive';

const DUE_COLOR = {
  late: 'error.main',
  today: 'warning.dark',
  soon: 'text.primary',
  later: 'text.secondary',
};

/**
 * 仓库作业: the warehouse's to-do list, by item and quantity, never money.
 *   - 待发货: sales ready to pick, most urgent first. Tick several and print their picking
 *     sheets in one go; once packed, the keeper scans the sheet in WeChat (or registers here).
 *   - 待收货: purchases the supplier has shipped, or self-recorded ones, to check in.
 * Each row has one action; everything else about an order stays with the office roles.
 */
export function Warehouse(p: Props) {
  const [view, setView] = useState<View>('ship');
  const [tasks, setTasks] = useState<WarehouseTasks | null>(null);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [shipping, setShipping] = useState<WarehouseTask | null>(null);
  const [posting, setPosting] = useState<WarehouseTask | null>(null);
  const [receiving, setReceiving] = useState<any>(null);
  const [printing, setPrinting] = useState(false);
  const { print, sheets } = usePickingSheets(p.current?.name ?? '');

  useEffect(() => {
    let live = true;
    api<WarehouseTasks>('/warehouse/tasks')
      .then((next) => {
        if (!live) return;
        setTasks(next);
        // Keep only ticks on orders still waiting.
        setPicked((ids) => ids.filter((id) => next.ship.some((t) => t.id === id)));
      })
      .catch((e) => p.setError(e.message));
    return () => {
      live = false;
    };
  }, [p.version, p.workspace]);

  const query = search.trim().toLowerCase();
  const matches = (t: WarehouseTask) =>
    !query ||
    `${t.number}${t.peer}${t.lines.map((l) => `${l.name}${l.spec}`).join('')}`
      .toLowerCase()
      .includes(query);
  const list = useMemo(() => (tasks?.[view] ?? []).filter(matches), [tasks, view, query]);
  const { pageItems, pager } = usePagination(list, `${view}|${query}`);
  const late = (v: View) =>
    (tasks?.[v] ?? []).filter((t) => deliveryLabel(t.deliveryDate).tone === 'late').length;

  const done = (message: string) => {
    setShipping(null);
    setPosting(null);
    setReceiving(null);
    p.reload();
    p.notify(message);
  };

  const printSheets = async () => {
    const chosen = (tasks?.ship ?? []).filter((t) => picked.includes(t.id));
    if (!chosen.length) return;
    setPrinting(true);
    try {
      // A sheet without its code still prints; the keeper then registers on the PC.
      const codes = await Promise.all(
        chosen.map((t) => imageUrl(`/warehouse/orders/${t.id}/code`).catch(() => null)),
      );
      if (codes.some((c) => !c)) p.notify('部分备货单未能生成小程序码，可在本页登记发货');
      print(chosen.map((task, i) => ({ task, qr: codes[i] })));
    } finally {
      setPrinting(false);
    }
  };

  const receive = (task: WarehouseTask) => {
    if (task.source === 'LOCAL') return setPosting(task);
    // A platform delivery is checked in batch by batch, as on the order page.
    api<any>(`/orders/${task.id}`)
      .then(setReceiving)
      .catch((e) => p.setError(e.message));
  };

  const shipView = view === 'ship';
  const pageIds = pageItems.map((t) => t.id);
  const allPicked = pageIds.length > 0 && pageIds.every((id) => picked.includes(id));
  const toggle = (id: string) =>
    setPicked((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  return (
    <>
      <Heading
        title="仓库作业"
        subtitle="按交期排好的发货与收货任务。"
        action={
          <Button
            variant="outlined"
            startIcon={<TvOutlined />}
            onClick={() => window.open('/board', 'picking-board')}
          >
            备货大屏
          </Button>
        }
      />
      <Paper>
        <ListToolbar>
          <CountTabs
            value={view}
            onChange={(v) => setView(v)}
            items={(['ship', 'receive'] as View[]).map((v) => ({
              value: v,
              label: v === 'ship' ? '待发货' : '待收货',
              count: tasks?.[v].length ?? 0,
              // Red when something is overdue; otherwise counts stay quiet.
              alert: late(v) ? 'error' : false,
            }))}
          />
          <Stack direction="row" spacing={1} sx={{ py: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder={`单号、${shipView ? '客户' : '供应商'}或商品`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 230 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            {shipView && (
              <Button
                variant="outlined"
                startIcon={<PrintOutlined />}
                disabled={!picked.length || printing}
                onClick={printSheets}
              >
                {printing
                  ? '正在生成…'
                  : picked.length
                    ? `打印备货单（${picked.length}）`
                    : '打印备货单'}
              </Button>
            )}
          </Stack>
        </ListToolbar>
        {!tasks ? null : !list.length ? (
          <Empty>
            {query
              ? '没有符合条件的订单。'
              : shipView
                ? '没有待发货的订单。双方确认后的销售单会出现在这里。'
                : '没有待收货的订单。供应商发货后会出现在这里。'}
          </Empty>
        ) : (
          <FitTableContainer>
            <Table
              stickyHeader
              sx={{
                minWidth: 760,
                '& th': { whiteSpace: 'nowrap' },
                '& th, & td': { px: 1.5 },
                '& th:last-of-type, & td:last-of-type': { pr: 2.5 },
                ...(shipView ? {} : { '& th:first-of-type, & td:first-of-type': { pl: 2.5 } }),
              }}
            >
              <TableHead>
                <TableRow>
                  {shipView && (
                    <TableCell padding="checkbox" sx={{ pl: 1 }}>
                      <Checkbox
                        size="small"
                        checked={allPicked}
                        indeterminate={!allPicked && pageIds.some((id) => picked.includes(id))}
                        onChange={() =>
                          setPicked((ids) =>
                            allPicked
                              ? ids.filter((id) => !pageIds.includes(id))
                              : [...new Set([...ids, ...pageIds])],
                          )
                        }
                        slotProps={{ input: { 'aria-label': '选择本页全部订单' } }}
                      />
                    </TableCell>
                  )}
                  <TableCell>交期</TableCell>
                  <TableCell>{shipView ? '客户 / 单号' : '供应商 / 单号'}</TableCell>
                  <TableCell>{shipView ? '待发商品' : '待收商品'}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {pageItems.map((task) => {
                  const due = deliveryLabel(task.deliveryDate);
                  const checked = picked.includes(task.id);
                  return (
                    <TableRow
                      key={task.id}
                      hover
                      selected={checked}
                      // Ticking is the common move on this list (to print a batch).
                      onClick={shipView ? () => toggle(task.id) : undefined}
                      sx={{ cursor: shipView ? 'pointer' : 'default', verticalAlign: 'top' }}
                    >
                      {shipView && (
                        <TableCell padding="checkbox" sx={{ pl: 1 }}>
                          <Checkbox
                            size="small"
                            checked={checked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggle(task.id)}
                            slotProps={{ input: { 'aria-label': `选择 ${task.number}` } }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ whiteSpace: 'nowrap', width: 110, py: 1.75 }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: due.tone === 'later' ? 400 : 650,
                            color: DUE_COLOR[due.tone],
                          }}
                        >
                          {due.text}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '28%', maxWidth: 0, py: 1.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 650, minWidth: 0 }} noWrap>
                            {task.peer}
                          </Typography>
                          {task.source === 'LOCAL' && <Tag>本企业登记</Tag>}
                        </Box>
                        <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }} noWrap>
                          {task.number}
                          {task.note && ` · ${task.note}`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Lines task={task} shipping={shipView} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 110, py: 1.5 }}>
                        <RowAction
                          label={shipView ? '登记发货' : '验收入库'}
                          onClick={() => (shipView ? setShipping(task) : receive(task))}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </FitTableContainer>
        )}
        {list.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              minHeight: 52,
              borderTop: '1px solid #e7edef',
            }}
          >
            <Typography color="text.secondary" sx={{ flex: 1, px: 2, py: 1.5, fontSize: 12 }}>
              {!pager && `共 ${list.length} 单 · `}
              {shipView
                ? '勾选订单后打印备货单；备完货用微信扫单上的码即可登记发货'
                : '按实际到货数量验收，有差异的只改那一行'}
            </Typography>
            {pager}
          </Box>
        )}
      </Paper>

      {shipping && (
        <LineQuantityDialog
          key={shipping.id}
          title="登记发货"
          subtitle={`${shipping.number} · ${shipping.peer} · 默认全部发出，只改实际少发的行`}
          verb="发货"
          lines={shipping.lines.map((l): QuantityLine => ({
            id: l.lineId,
            name: l.name,
            spec: l.spec,
            unit: l.unit,
            ordered: l.quantity,
            done: l.done,
            remaining: l.remaining,
            skuId: l.skuId,
            accessories: l.accessories,
          }))}
          warehouses={p.warehouses}
          footnote="提交后扣减本企业库存并通知客户。"
          submit={(warehouseId, lines, key) =>
            api(`/warehouse/orders/${shipping.id}/ship`, { warehouseId, lines, key })
          }
          onClose={() => setShipping(null)}
          onDone={() => done('已登记发货')}
        />
      )}
      {posting && (
        <LineQuantityDialog
          key={posting.id}
          title="验收入库"
          subtitle={`${posting.number} · ${posting.peer} · 按实际数量登记，可分批完成`}
          verb="入库"
          lines={posting.lines.map((l): QuantityLine => ({
            id: l.lineId,
            name: l.name,
            spec: l.spec,
            unit: l.unit,
            ordered: l.quantity,
            done: l.done,
            remaining: l.remaining,
          }))}
          warehouses={p.warehouses}
          footnote="提交后增加本企业库存并写入流水。"
          submit={(warehouseId, lines, key) =>
            api(`/local-orders/${posting.id}/post`, { warehouseId, lines, key })
          }
          onClose={() => setPosting(null)}
          onDone={() => done('已验收入库')}
        />
      )}
      {receiving && (
        <ReceiveDialog
          order={receiving}
          warehouses={p.warehouses}
          onClose={() => setReceiving(null)}
          onDone={() => done('已登记收货')}
        />
      )}
      {sheets}
    </>
  );
}

/** What is left to move, one item per line (three at most, then a count). */
function Lines({ task, shipping }: { task: WarehouseTask; shipping: boolean }) {
  const shown = task.lines.slice(0, 3);
  return (
    <>
      {shown.map((l) => (
        <Box key={l.lineId} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, minWidth: 0 }} noWrap>
            {l.name}
            {l.spec && (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {' '}
                {l.spec}
              </Box>
            )}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 650, whiteSpace: 'nowrap', ml: 'auto' }}>
            {amount(l.remaining)} {l.unit}
          </Typography>
        </Box>
      ))}
      {task.lines.length > shown.length && (
        <Typography color="text.secondary" sx={{ fontSize: 12 }}>
          另有 {task.lines.length - shown.length} 种商品
        </Typography>
      )}
      {shipping && task.lines.some((l) => l.accessories.length) && (
        <Typography color="text.secondary" sx={{ fontSize: 12 }} noWrap>
          随附：
          {task.lines
            .filter((l) => l.accessories.length)
            .map((l) => accessoriesText(l.accessories, l.remaining))
            .join('、')}
        </Typography>
      )}
    </>
  );
}
